// store/modules/config.js
import { getWebViewConfig, getActivityControls } from '@/api/config'
import { getWebConfig, isWebConfigChanged, setWebConfig } from '@/utils/config'

const state = {
    webViewConfig: null,
    activityFlags: {},
}

const mutations = {
    SET_WEB_VIEW_CONFIG(state, config) {
        state.webViewConfig = config
    },
    SET_ACTIVITY_FLAGS(state, flags) {
        state.activityFlags = flags || {}
    },
}

const getters = {
    isActivityEnabled: (state) => (code, defaultEnabled = false) => {
        const flags = state.activityFlags || {}
        if (!code) return defaultEnabled
        if (Object.prototype.hasOwnProperty.call(flags, code)) {
            return !!flags[code]
        }
        // 后续新活动：未在后台配置则默认关闭，避免更新错开提前露出
        return defaultEnabled
    },
}

const actions = {
    async getWebViewConfig({ commit }) {
        try {
            const response = await getWebViewConfig()
            setWebConfig(response.data)
            commit('SET_WEB_VIEW_CONFIG', response.data)
            if (response.data?.activity_flags) {
                commit('SET_ACTIVITY_FLAGS', response.data.activity_flags)
            }
            return response
        } catch (error) {
            console.error('获取网站设置失败:', error)
            throw error
        }
    },

    async fetchActivityControls({ commit }) {
        try {
            const response = await getActivityControls({ force: true })
            const flags = response.data?.activity_flags || {}
            commit('SET_ACTIVITY_FLAGS', flags)
            return flags
        } catch (error) {
            console.error('获取活动开关失败:', error)
            return {}
        }
    },

    // 启动时：先用缓存（秒开），再后台拉新比对是否需要更新
    async bootstrapWebViewConfig({ commit, dispatch, state }) {
        const cached = getWebConfig()
        if (cached) {
            commit('SET_WEB_VIEW_CONFIG', cached)
            if (cached.activity_flags) {
                commit('SET_ACTIVITY_FLAGS', cached.activity_flags)
            }
        }

        // 后台刷新：有变化才更新缓存与store
        try {
            const response = await dispatch('refreshWebViewConfigIfNeeded')
            // 再单独拉一次活动开关，保证开关可独立热更新
            await dispatch('fetchActivityControls')
            return { cachedUsed: !!cached, ...response }
        } catch (e) {
            // 无缓存且接口失败/超时时，写入空配置避免一直停在「加载中」
            if (!cached && !state.webViewConfig) {
                commit('SET_WEB_VIEW_CONFIG', { page_menu_list: [] })
            }
            return { cachedUsed: !!cached, updated: false, error: e }
        }
    },

    async refreshWebViewConfigIfNeeded({ commit }) {
        const oldConfig = getWebConfig()
        const response = await getWebViewConfig({ force: true })
        const newConfig = response.data

        const changed = isWebConfigChanged(oldConfig, newConfig)
        if (changed) {
            setWebConfig(newConfig)
            commit('SET_WEB_VIEW_CONFIG', newConfig)
            if (newConfig?.activity_flags) {
                commit('SET_ACTIVITY_FLAGS', newConfig.activity_flags)
            }

            // 给站点一个“配置已更新”的信号，业务需要时可监听做刷新/提示
            try {
                window.dispatchEvent(
                    new CustomEvent('webconfig:updated', {
                        detail: { oldConfig, newConfig }
                    })
                )
            } catch (e) {
                // ignore
            }
        } else if (newConfig?.activity_flags) {
            commit('SET_ACTIVITY_FLAGS', newConfig.activity_flags)
        }

        return { updated: changed, response }
    },
}

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
}
